import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSketchStore, createSketchSolid } from '../../state/useSketchStore';
import { useSketchUiStore } from '../../state/useSketchUiStore';
import { circularLoop } from '../../core/sketch/curves';
import { buildText3DSolid } from '../../core/sketch/text3d';
import type { InferenceResult } from '../../core/sketch/inference';
import { useSketchToolEnv } from './sketchInput';
import { registerSketchTool } from './sketchToolBus';

/**
 * Construction tools — Protractor, Dimension, Text (2D label), 3D Text and
 * the drawing Axes, completing the SketchUp construction set. Measure
 * results report into the VCB box; Text tools hand string entry to the DOM
 * prompt overlay through useSketchUiStore.
 */

const newId = (): string => `ann-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const GUIDE_EXTENT = 1000;

// ============================== PROTRACTOR ==============================

/**
 * Protractor: center → reference direction → measured direction. Reports
 * the angle in degrees into the VCB; holding Ctrl on the final click drops
 * an infinite angled guide line through the center (Sketch's guide protractor).
 */
export function useSketchProtractorTool() {
    const env = useSketchToolEnv();
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [refDir, setRefDir] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, center ? [center] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, center ? [center] : [], null);
                if (!center) {
                    setCenter(res.point.clone());
                    return;
                }
                if (!refDir) {
                    if (res.point.distanceTo(center) < 1e-6) return;
                    setRefDir(res.point.clone().sub(center).setZ(0).normalize());
                    return;
                }
                // Third click: measured angle (Z-plane, like the Rotate tool).
                const curDir = res.point.clone().sub(center).setZ(0);
                if (curDir.lengthSq() > 1e-12 && refDir.lengthSq() > 1e-12) {
                    let angle = refDir.clone().normalize().angleTo(curDir.clone().normalize());
                    const cross = refDir.x * curDir.y - refDir.y * curDir.x;
                    if (cross < 0) angle = -angle;
                    const vcb = env.consumeVcb();
                    const finalAngle = vcb !== null ? vcb : (angle * 180) / Math.PI;
                    useSketchStore.getState().setVcbText(`${finalAngle.toFixed(2)}°`);
                    if (hit.ctrlKey) {
                        // Angled guide: infinite line through the center at
                        // the measured angle.
                        const rad = (finalAngle * Math.PI) / 180;
                        const dir = new THREE.Vector3(Math.cos(rad), Math.sin(rad), 0);
                        useSketchStore.getState().addGuide({
                            id: newId(),
                            kind: 'line',
                            a: [center.x - dir.x * GUIDE_EXTENT, center.y - dir.y * GUIDE_EXTENT, center.z],
                            b: [center.x + dir.x * GUIDE_EXTENT, center.y + dir.y * GUIDE_EXTENT, center.z],
                        }, 'Add Angled Guide');
                    }
                }
                setCenter(null);
                setRefDir(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, center, refDir]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCenter(null);
            setRefDir(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { center, refDir, cursor };
}

// ============================== DIMENSION ==============================

/**
 * Dimension: start point → end point → offset line position. The committed
 * annotation renders with witness lines and the measured value; the live
 * length reports into the VCB while picking.
 */
export function useSketchDimensionTool() {
    const env = useSketchToolEnv();
    const [a, setA] = useState<THREE.Vector3 | null>(null);
    const [b, setB] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, [], null);
                const point = res.point.clone();
                if (!a) {
                    setA(point);
                    return;
                }
                if (!b) {
                    if (point.distanceTo(a) < 1e-6) return;
                    setB(point);
                    useSketchStore.getState().setVcbText(point.distanceTo(a).toFixed(3));
                    return;
                }
                // Third click places the dim line offset; the annotation keeps
                // the measured length (per-axis VCB text override is v2).
                const store = useSketchStore.getState();
                const value = a.distanceTo(b);
                store.addDimension({ a: [a.x, a.y, a.z], b: [b.x, b.y, b.z], offset: [point.x, point.y, point.z], value }, 'Dimension');
                setA(null);
                setB(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, a, b]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setA(null);
            setB(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { a, b, cursor };
}

// ============================== TEXT (2D LABEL) ==============================

/**
 * Text: click a model point, type the label in the prompt overlay, Enter
 * commits the annotation with a short leader line.
 */
export function useSketchTextTool() {
    const env = useSketchToolEnv();
    const pendingAnchor = useRef<[number, number, number] | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => setCursor(env.infer(hit, [], null)),
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, [], null);
                const p = res.point.clone();
                pendingAnchor.current = [p.x, p.y, p.z];
                useSketchUiStore.getState().openTextDraft({
                    kind: 'label',
                    anchor: pendingAnchor.current,
                    screen: hit.pointerPx ?? { x: 0, y: 0 },
                });
            },
        });
        return () => registerSketchTool(null);
    }, [env]);

    // Consume the prompt's committed text (label anchor stays pending until
    // the overlay returns).
    useEffect(() => useSketchUiStore.subscribe((state) => {
        if (!state.textCommit || state.textCommit.kind !== 'label') return;
        const anchor = pendingAnchor.current;
        const commit = state.consumeTextCommit();
        if (!anchor || !commit) return;
        const p = new THREE.Vector3(...anchor);
        // Leader end: anchored to the camera-right + up offset so the tag
        // reads beside the point instead of covering it.
        const right = new THREE.Vector3().setFromMatrixColumn(env.camera.matrixWorld, 0).setZ(0).normalize();
        const leader = p.clone().addScaledVector(right, 1.2).add(new THREE.Vector3(0, 0, 0.8));
        useSketchStore.getState().addLabel({
            anchor: [p.x, p.y, p.z],
            leader: [leader.x, leader.y, leader.z],
            text: commit.text,
        }, 'Text');
        pendingAnchor.current = null;
    }), [env]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            pendingAnchor.current = null;
            useSketchUiStore.getState().closeTextDraft();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { cursor };
}

// ============================== 3D TEXT ==============================

/**
 * 3D Text: click the placement point, enter text + height/depth in the
 * prompt, Enter extrudes the string into a solid on the active plane.
 */
export function useSketchText3dTool() {
    const env = useSketchToolEnv();
    const pending = useRef<{ anchor: [number, number, number]; normal: THREE.Vector3 } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);
    const [ghost, setGhost] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, [], null);
                setCursor(res);
                setGhost(res.point.clone());
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, [], null);
                const p = res.point.clone();
                const plane = env.planeOf(hit);
                pending.current = {
                    anchor: [p.x, p.y, p.z],
                    normal: plane.normal.clone().normalize(),
                };
                useSketchUiStore.getState().openTextDraft({
                    kind: 'text3d',
                    anchor: pending.current.anchor,
                    screen: hit.pointerPx ?? { x: 0, y: 0 },
                    height: 1,
                    depth: 0.25,
                });
            },
        });
        return () => registerSketchTool(null);
    }, [env]);

    useEffect(() => useSketchUiStore.subscribe((state) => {
        if (!state.textCommit || state.textCommit.kind !== 'text3d') return;
        const pendingRef = pending.current;
        const commit = state.consumeTextCommit();
        if (!pendingRef || !commit) return;
        const built = buildText3DSolid({
            text: commit.text,
            height: commit.height,
            depth: commit.depth,
            origin: new THREE.Vector3(...pendingRef.anchor),
            normal: pendingRef.normal,
        });
        if (built) {
            useSketchStore.getState().addSolid(
                createSketchSolid(built.faces, built.name),
                '3D Text'
            );
        }
        pending.current = null;
    }), []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            pending.current = null;
            useSketchUiStore.getState().closeTextDraft();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { cursor, ghost };
}

// ============================== AXES ==============================

/**
 * Axes: origin → X direction → Y direction re-places the drawing axes (the
 * default drawing plane follows). Alt+click restores the world ground.
 */
export function useSketchAxesTool() {
    const env = useSketchToolEnv();
    const [origin, setOrigin] = useState<THREE.Vector3 | null>(null);
    const [xPoint, setXPoint] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, origin ? [origin] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                if (hit.altKey) {
                    // Reset: back to the world Z=0 ground.
                    useSketchStore.getState().setDrawingAxes(null);
                    setOrigin(null);
                    setXPoint(null);
                    return;
                }
                const res = env.infer(hit, origin ? [origin] : [], null);
                if (!origin) {
                    setOrigin(res.point.clone());
                    return;
                }
                if (!xPoint) {
                    if (res.point.distanceTo(origin) < 1e-6) return;
                    setXPoint(res.point.clone());
                    return;
                }
                const xAxis = xPoint.clone().sub(origin);
                const yAxis = res.point.clone().sub(origin);
                if (xAxis.lengthSq() < 1e-12 || yAxis.lengthSq() < 1e-12) return;
                useSketchStore.getState().setDrawingAxes({
                    origin: [origin.x, origin.y, origin.z],
                    xAxis: [xAxis.x, xAxis.y, xAxis.z],
                    yAxis: [yAxis.x, yAxis.y, yAxis.z],
                });
                setOrigin(null);
                setXPoint(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, origin, xPoint]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setOrigin(null);
            setXPoint(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { origin, xPoint, cursor };
}

/** Shared preview helper: arc sweep between two directions (protractor). */
export function protractorArcPreview(
    center: THREE.Vector3,
    refDir: THREE.Vector3,
    cursor: THREE.Vector3,
    segments = 24
): THREE.Vector3[] | null {
    const curDir = cursor.clone().sub(center).setZ(0);
    if (curDir.lengthSq() < 1e-12) return null;
    const radius = Math.max(curDir.length(), 0.5);
    const a0 = Math.atan2(refDir.y, refDir.x);
    const a1 = Math.atan2(curDir.y, curDir.x);
    let sweep = a1 - a0;
    while (sweep <= -Math.PI) sweep += Math.PI * 2;
    while (sweep > Math.PI) sweep -= Math.PI * 2;
    return circularLoop(center, radius, new THREE.Vector3(0, 0, 1), a0, a0 + sweep, segments);
}
